import React, { useEffect, useRef, useState, useCallback } from "react";
import "./Carousel.css";
import { motion } from "framer-motion";
import { useTranslation } from "react-i18next";

const DRAG_THRESHOLD = 80;
const AUTO_DELAY = 12000;

const SPRING = {
  type: "spring",
  stiffness: 400,
  damping: 50,
};

const wrap = (i, len) => ((i % len) + len) % len;

function Carousel() {
  const { t, i18n } = useTranslation("hobbies");
  const images = t("carousel", { returnObjects: true });

  const [index, setIndex] = useState(0);
  const autoplayRef = useRef(null);

  const paginate = useCallback((dir) => {
    setIndex((i) => wrap(i + dir, images.length));
  }, [images.length]);

  useEffect(() => {
    autoplayRef.current = setInterval(() => paginate(1), AUTO_DELAY);
    return () => clearInterval(autoplayRef.current);
  }, [paginate]);

  const resetAutoplay = () => {
    clearInterval(autoplayRef.current);
    autoplayRef.current = setInterval(() => paginate(1), AUTO_DELAY);
  };

  const overlayAnimationVariants = {
    initial: { opacity: 0, x: -64 },
    animate: (delay) => ({
      opacity: 1,
      x: 0,
      transition: { duration: 0.4, delay: 0.08 * delay },
    }),
  };

  const Italicizer = ({ text, target }) => {
    const i = text.indexOf(target);
    if (i === -1) return <>{text}</>;
    return (
      <>
        {text.slice(0, i)}
        <i>{target}</i>
        {text.slice(i + target.length)}
      </>
    );
  };

  return (
    <div id="carousel">
      {images.map((image, i) => {
        let offset = i - index;
        if (offset > images.length / 2) offset -= images.length;
        if (offset < -images.length / 2) offset += images.length;

        return (
          <motion.div
            key={i}
            className="carousel-card"
            drag="x"
            dragConstraints={{ left: 0, right: 0 }}
            onPointerDown={resetAutoplay}
            onDragEnd={(_, info) => {
              if (info.offset.x < -DRAG_THRESHOLD) paginate(1);
              if (info.offset.x > DRAG_THRESHOLD) paginate(-1);
            }}
            animate={{
              x: `${offset * 100}%`,
              scale: offset === 0 ? 0.95 : 0.85,
              opacity: Math.abs(offset) > 1 ? 0 : 1,
              zIndex: offset === 0 ? 1 : 0,
            }}
            transition={SPRING}
            style={{
              backgroundImage: `url(${image.image})`,
            }}
          >
            {offset === 0 && (
              <motion.div
                className="carousel-overlay"
                variants={overlayAnimationVariants}
                initial="initial"
                animate="animate"
                custom={1}
              >
                <motion.h3 custom={2} variants={overlayAnimationVariants}>
                  {t("carouselTitle")}
                </motion.h3>

                <motion.h1 custom={3} variants={overlayAnimationVariants}>
                  {image.location.toUpperCase()}
                </motion.h1>

                <motion.p className="carousel-date" custom={4} variants={overlayAnimationVariants}>
                  {image.date}
                </motion.p>

                <motion.p className="carousel-story" custom={5} variants={overlayAnimationVariants}>
                  {i18n.language === "en" ? (
                    <Italicizer
                      text={image.story}
                      target="The Persistence of Memory"
                    />
                  ) : (
                    image.story
                  )}
                </motion.p>

              </motion.div>
            )}
          </motion.div>
        );
      })}

      <div id="carousel-dots">
        {images.map((_, i) => (
          <button
            key={i}
            onClick={() => {
              setIndex(i);
              resetAutoplay();
            }}
            className={i === index ? "selected" : ""}
          />
        ))}
      </div>
    </div>
  );
}

export default Carousel;